import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePersistentChoice } from "./persistent-flag.ts";
import {
  appearanceOf,
  followsSystem,
  parseTheme,
  THEME_KEY,
  type Appearance,
  type ThemeChoice,
} from "./theme.ts";

/**
 * Тема приложения: хранение выбора, слежение за системой и атрибут на корне.
 *
 * Решение, ЧТО показывать, живёт не здесь, а в `theme.ts`, чистой функцией с
 * тестами. Здесь только браузерная половина: localStorage, `matchMedia` и
 * `document.documentElement`. Так же разведено всё, что хранится в браузере и
 * разбирается раньше первого рендера.
 *
 * ⚠️ **Провайдером, а не хуком на каждого желающего.** Выбор нужен сразу в
 * четырёх местах: переключателю в кабинете, переключателю за шестерёнкой в
 * комнате, доске урока и доске архива. `usePersistentChoice` держит состояние
 * ПО КОМПОНЕНТУ, поэтому четыре его вызова означали бы четыре независимых
 * копии: человек жмёт «светлая» в меню комнаты, страница светлеет, а доска
 * остаётся тёмной. Сам хук при этом никуда не делся, он стоит внутри
 * провайдера ровно один раз, и способ хранения тот же, что у угла видео и
 * панели метрик (решение владельца 02.09.2026).
 *
 * ⚠️ Провайдер стоит ВЫШЕ маршрутизатора: комната, архив и погашение ссылки
 * живут вне каркаса кабинета намеренно, и провайдер, повешенный внутри
 * `Shell`, отсутствовал бы ровно на доске, ради которой тема и переключается.
 */

const DARK = "(prefers-color-scheme: dark)";

/**
 * Что стоит в системе прямо сейчас.
 *
 * ⚠️ Отказ читается как светлая, а не как тёмная: браузер, не умеющий
 * `matchMedia`, обязан приземлиться на умолчание приложения (`DEFAULT_THEME`,
 * решение владельца 17.09.2026), а не на тему, противоположную ему. До
 * 17.09.2026 здесь стояла тёмная, и доводом было «приложение было таким до
 * 02.09.2026»: довод умер вместе с прежним умолчанием.
 */
function readSystem(): Appearance {
  try {
    return window.matchMedia(DARK).matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

type ThemeApi = {
  choice: ThemeChoice;
  setChoice: (next: ThemeChoice) => void;
  /** Что видно на экране прямо сейчас. Это же уезжает в Excalidraw. */
  appearance: Appearance;
  /**
   * Приколотить тему на время экрана. Возвращает снятие, поэтому зовётся
   * прямо из `useEffect`. Разбор у `useFrozenTheme` ниже.
   */
  freeze: () => () => void;
};

const Ctx = createContext<ThemeApi | null>(null);

export function ThemeProvider(props: { children: React.ReactNode }) {
  const [choice, setChoice] = usePersistentChoice(THEME_KEY, parseTheme);
  const [system, setSystem] = useState<Appearance>(readSystem);
  /** Сколько открытых экранов просят не следить за системой (`followsSystem`). */
  const [frozen, setFrozen] = useState(0);

  /** Что видно на экране. Одно правило на страницу, на холст и на атрибут. */
  const appearance = appearanceOf(choice, system);

  /**
   * Слежение за системной темой.
   *
   * ⚠️ Пока открыт урок, слежения нет вовсе, и это требование, а не экономия:
   * у человека с автоматическим переходом на тёмную по закату доска
   * перекрасилась бы в середине занятия. Правило записано отдельной чистой
   * функцией `followsSystem`, там же разбор.
   *
   * ⚠️ При закрытии урока эффект заводится заново и ПЕРВЫМ ДЕЛОМ перечитывает
   * систему, а не ждёт следующего события: смена, случившаяся во время урока,
   * иначе не доехала бы никогда.
   */
  useEffect(() => {
    if (!followsSystem(frozen)) return;
    let media: MediaQueryList;
    try {
      media = window.matchMedia(DARK);
    } catch {
      return;
    }
    const sync = () => setSystem(media.matches ? "dark" : "light");
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [frozen]);

  /**
   * Атрибут на `<html>`.
   *
   * ⚠️ Пишется ВСЕГДА и никогда не снимается: медиазапроса под тему в
   * `styles.css` нет, поэтому снятый атрибут означал бы тёмную у всех, включая
   * выбравшего «как в системе» на светлом ноутбуке.
   *
   * ⚠️ Первую отрисовку этот эффект НЕ охраняет: он работает уже после неё.
   * Атрибут до первого кадра ставит крошечный `public/boot-theme.js`, иначе при
   * светлой теме первым кадром мелькала бы чернота. Здесь только последующие
   * смены, и первая запись обязана совпасть с тем, что сделал скрипт, иначе
   * первое же применение эффекта дало бы мигание на ровном месте. Совпадает
   * она потому, что правило одно: `appearanceOf` и там, и тут.
   */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", appearance);
  }, [appearance]);

  const freeze = useCallback(() => {
    setFrozen((n) => n + 1);
    return () => setFrozen((n) => n - 1);
  }, []);

  const value = useMemo<ThemeApi>(
    () => ({ choice, setChoice, appearance, freeze }),
    [choice, setChoice, appearance, freeze],
  );

  return <Ctx.Provider value={value}>{props.children}</Ctx.Provider>;
}

function useThemeApi(): ThemeApi {
  const api = useContext(Ctx);
  if (api === null) throw new Error("ThemeProvider не поставлен");
  return api;
}

/** Выбор человека и то, чем его менять. Нужен только двум переключателям. */
export function useThemeChoice(): [ThemeChoice, (next: ThemeChoice) => void] {
  const { choice, setChoice } = useThemeApi();
  return [choice, setChoice];
}

/**
 * Что показывать. Отсюда доски берут `theme` для Excalidraw, поэтому страница
 * и холст разойтись не могут: значение у них одно.
 */
export function useAppearance(): Appearance {
  return useThemeApi().appearance;
}

/**
 * Экран, на котором тема не меняется сама.
 *
 * Зовёт его комната урока, и только она. Пока она открыта, «как в системе»
 * означает «как было в системе, когда урок открыли»: доска, перекрасившаяся
 * посреди занятия по закату, это ровно та мелкая поломка, которую человек
 * запомнит как «у них всё мигает».
 *
 * ⚠️ Архив сюда не входит намеренно: там смотрят прошлое занятие, и смена
 * темы под курсором ничему не мешает.
 */
export function useFrozenTheme(): void {
  const { freeze } = useThemeApi();
  useEffect(() => freeze(), [freeze]);
}
